import { DRIVER_LABELS } from './driverTerms';
// Forecast V3 Type Definitions

// ============ Driver Types ============
// All driver types supported by the backend
export type DriverType =
  // Manual/Static
  | 'Manual'
  | 'Static Entry'
  // Budget
  | 'LY Published Budget'
  | 'LY Published Budget $POR'
  | 'LY Published Budget $PAR'
  | '% of Total Revenue (LY Budget)'
  | '% of Selected Revenue (LY Budget)'
  | 'Budget'
  // Room-based
  | '$ / ORN'
  | '$ / ARN'
  | 'REVPAR Projection' // retired — legacy rows only
  | 'Occupancy Forecast'
  // Last Month
  | '$ LM'
  | '$ / ORN LM'
  | '$ / ARN LM'
  // External Sources
  | 'OTB Forecast'
  | 'RMS Forecast'
  | 'Room Forecast'
  // Revenue % (flexible, allows selecting departments)
  | '% of Revenue'
  | '% of Selected Revenue'
  // Account-based % driver
  | '% of Account'
  // Last Year / Historical
  | 'LY Same Month'
  | 'Seasonal Budget Spread'
  | 'YoY Growth %'
  | '$ / ORN LY'
  | '$ / ARN LY'
  // Budget-based drivers
  | '% of Selected Revenue (Budgeted)'
  | '% of Total Revenue (Budgeted)'
  | 'Budgeted $POR'
  | 'Budgeted $PAR'
  // Labor drivers
  | 'HPOR'            // Hours Per Occupied Room (deprecated)
  | 'DPOR'            // Dollars Per Occupied Room (deprecated)
  | '$POR Projection' // Dollars Per Occupied Room (current)
  | 'Avg Rate x Hours' // Average Rate × Projected Hours (deprecated)
  | 'Labor Config'    // Per-position driver (min/occ, hrs/occ, % revenue)
  // Zero-base composable driver
  | 'Zero Base';

export type VersionStatus = 'Draft' | 'Locked' | 'Published';
export type VersionType = 'Forecast' | 'Budget';
export type AssumptionType = 'Scalar' | 'Series';
export type AssumptionScope = 'Global' | 'Property' | 'Department';
export type DataSource = 'Manual' | 'PMS' | 'RMS' | 'Budget' | 'Accounting';
export type HealthStatus = 'good' | 'warning' | 'error';
export type AccountType = 'Revenue' | 'Expense';

// ============ Core Domain Types ============

export interface Location {
  id: number;
  name: string;
  orgId: number;
  code?: string;
}

export interface ForecastVersion {
  budgetReferenceYear?: number | null;
  id: number;
  name: string;
  type: VersionType;
  status: VersionStatus;
  createdFrom: string | null;
  createdAt: string;
  owner: string;
  coverage: string;
  orgId: number;
  locationId: number;
  year: number;                           // The year this version covers
  description?: string;
  versionType?: 'forecast' | 'budget';    // Type of version
  isDefault?: boolean;                    // Whether this is the default version
  lockedMonths?: number[];                // Months that are locked (1-12)
  roomStatSource?: 'pms' | 'budget' | 'actuals';  // Source for room statistics
  actualRollInEnabled?: boolean;          // Workspace toggle: Actuals + Forecast vs Forecast only
  startDate?: string;
  endDate?: string;
}

export interface MonthlyValue {
  monthIndex: number; // 0-11 (Jan-Dec)
  monthKey: string; // 'jan', 'feb', etc.
  budget: number;
  actual: number;
  forecast: number;
  override: number | null;
  isOverridden: boolean;
}

// Explanation factors returned by the calculate API for each month
export interface ExplanationFactors {
  isLocked?: boolean;
  overrideAmount?: number;
  dailyTotal?: number;
  pinnedTotal?: number;
  usedFallback?: boolean;
  calculationSource?: string;
  hasInputOverrides?: boolean;
  driverAmount?: number;
  finalAmount?: number;
  rateOverride?: number;
  // Room-based driver factors
  occupiedRooms?: number;
  availableRooms?: number;
  daysWithData?: number;
  daysInMonth?: number;
  projectedRooms?: number;
  inputValue?: number;
  // Revenue % factors
  totalLocationRevenue?: number;
  roomsRevenue?: number;
  fbRevenue?: number;
  departmentRevenue?: number;
  // Selected Revenue driver factors
  selectedDepartments?: string[];  // For % of Selected Revenue driver
  totalSelectedRevenue?: number;   // Sum of selected department revenues
  revenueByDepartment?: Record<string, number>;  // Breakdown by department
  // Historical factors
  lyBudget?: number;
  lyBudgetOccupiedRooms?: number;
  lyActual?: number;
  previousMonthActual?: number;
  budget?: number;
  // External source factors (OTB/RMS)
  otbRoomRevenue?: number;   // hotelRevenue where mappedType = 'Room Revenue'
  otbTrnRevenue?: number;    // vw_latestForecastOcc.otbTrnRevenue (transient)
  otbGrpRevenue?: number;    // vw_latestForecastOcc.otbGrpRevenue (group)
  otbForecast?: number;      // Sum of above three
  rmsForecast?: number;
  // Pre-formatted calculation steps from API
  steps?: string[];
  formula?: string;
  // Labor driver factors
  projectedHours?: number;   // Calculated hours for HPOR driver
  avgHourlyRate?: number;    // Average hourly rate from PayrollCost
  dporTarget?: number;       // DPOR target rate
  projectedOccupiedRooms?: number;  // Projected occupied rooms for DPOR
  mtdPaceData?: {            // MTD pacing data for current month
    actualCostMtd: number;
    actualHoursMtd?: number;
    daysElapsed?: number;
    daysRemaining?: number;
    projectedRemainingHours?: number;
    projectedRemainingCost: number;
  };
  // Zero Base driver factors
  layerResults?: Array<{ id: number; type: string; description: string; value: number; amount: number }>;
  layerCount?: number;
}

// Expanded values for each month (for Budget/Actual/Forecast columns)
export interface MonthlyExpandedValues {
  forecast: number;      // Final forecast value (after adjustments)
  baseAmount: number;    // Pre-adjustment driver value
  budget: number;        // Budget amount
  actual: number;        // Actual amount
  lyBudget?: number;
  lyBudgetOccupiedRooms?: number;
  lyActual: number;      // Last year same month actual
  current: number;       // Actual for past, Forecast for future
  isActualRolledIn?: boolean; // Collapsed view is displaying actual for this closed month
  hasOverride: boolean;  // Whether month has manual override
  hasAdjustments?: boolean;  // Whether adjustments were applied this month
  adjustmentsTotal?: number; // Total adjustment amount for this month
}

// ============ Zero Base Driver Types ============

export type ZeroBaseLayerType = 'fixed' | 'pct_revenue' | 'pct_other' | 'por' | 'par';

export interface ZeroBaseDriverLayer {
  id: number;
  type: ZeroBaseLayerType;
  description: string;
  value: number | null;
  sourceChartOfAccountIds?: number[]; // For 'pct_other' — which GL accounts to reference (summed)
  // Present only on the calculator's per-month EFFECTIVE copy when a
  // driver-level monthly override replaced the saved value (holds the saved value)
  overrides?: { value?: number | null };
}

// ============ Driver-level monthly overrides ============

export type DriverInputOverrideField = 'input' | 'value' | 'hourlyRate';

/** One override row: layerKey '' = the account driver's own rate; otherwise a Zero Base layer id / Labor Config positionId */
export interface DriverInputOverrideItem {
  chartOfAccountId: number;
  year: number;
  month: number; // 1-12
  layerKey: string;
  field: DriverInputOverrideField;
  value: number | null; // null clears
  notes?: string | null;
}

export interface DriverInputOverrideRow {
  id?: number;
  layerKey: string;
  field: DriverInputOverrideField;
  value: number;
  notes?: string | null;
}

/** The rate a single-rate driver applied this month (from driverInputOverrides.js RATE_SPECS) */
export interface DriverRateInfo {
  mode: 'input' | 'derived';
  unit: 'currency' | 'percent';
  basis: 'orn' | 'arn' | 'total_revenue' | 'selected_revenue' | 'source_accounts' | 'ly_actual' | null;
  basisValue: number | null;
  label: string;
  rate: number;
  overridden: boolean;
  baseRate: number;
  /** Realized rate (actual $ ÷ actual basis) when the month has actuals */
  actualRate?: number | null;
}

// ============ Labor Config Driver Types ============

export type LaborPositionDriverType = 'min_por' | 'hrs_por' | 'fte' | 'pct_revenue';
export type LaborRevenueScope = 'total' | 'department' | 'account';

export interface LaborPositionConfig {
  positionId:       number;
  positionName:     string;
  departmentName:   string;
  driverType:       LaborPositionDriverType;
  value:            number | null;
  // Hourly rate used to cost MPOR/HPOR/FTE hours. Auto-populated from recent
  // payroll actuals; user-adjustable. Null = let the backend resolve it.
  hourlyRate?:      number | null;
  // For pct_revenue only:
  revenueScope:     LaborRevenueScope;
  revenueDeptName?:   string;   // when scope = 'department'
  revenueAccountId?:  number;   // legacy single account (saved configs pre multi-select)
  revenueAccountIds?: number[]; // when scope = 'account' — selected accounts, summed
  // Present only on the calculator's per-month EFFECTIVE copy when a
  // driver-level monthly override replaced value / hourlyRate (holds the saved values)
  overrides?: { value?: number | null; hourlyRate?: number | null };
}

export interface LaborPosition {
  id:              number;
  name:            string;
  departmentName:  string;
  hpor?:           number;   // historical HPOR hint for default value
  dpor?:           number;   // historical DPOR hint
  avgHourlyRate?:  number;   // for cost preview
}

export interface ForecastAccount {
  id: number;
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  accountType: AccountType;
  department: string;
  departmentId?: number;
  driverType: DriverType;
  driverInput: number | null;
  monthlyValues: Record<string, number>; // { jan: 1234, feb: 5678, ... }
  total: number;
  isExpanded?: boolean;
  // Explanation factors per month (for "How X was calculated" display)
  explanationFactors?: Record<string, ExplanationFactors>; // { jan: {...}, feb: {...}, ... }
  // Track which months have override values (true = override is set)
  monthlyOverrides?: Record<string, boolean>; // { jan: true, feb: false, ... }
  // Expanded values for Budget/Actual/Forecast view
  monthlyExpanded?: Record<string, MonthlyExpandedValues>; // { jan: {...}, feb: {...}, ... }
  // Adjustment tracking
  hasAdjustments?: boolean;                    // Whether account has any active adjustments
  adjustmentsCount?: number;                   // Number of active adjustments
  adjustmentsTotal?: number;                   // Total adjustment amount (for any month)
  // Daily values tracking
  hasDailyValues?: boolean;                    // Whether account has any daily values
  monthlyDailyValues?: Record<string, boolean>; // Track which months have daily values { jan: true, feb: false, ... }
  // 'blended' = pinned days + live driver days; 'legacy' = full-month static detach
  monthlyDailyMode?: Record<string, 'blended' | 'legacy'>;
  monthlyPinnedCounts?: Record<string, number>; // Pinned-day count per blended month
  // Set by the calculator when the assigned driver is not eligible for this
  // account (e.g. a revenue account on "% of Total Revenue" — circular). The
  // month forecasts $0 and the reason is human-readable.
  driverError?: string | null;
  // Department driver inheritance
  departmentDriverType?: DepartmentDriverType | null; // Driver set at department level
  inheritsFromDepartment?: boolean;            // True if using department driver (no account-level driver)
  driverSource?: 'account' | 'department' | 'none'; // Where the effective driver came from
  // Fallback driver info (when department driver has OTB/RMS with fallback configured)
  usingFallbackDriver?: boolean;               // True if currently using fallback (outside OTB window)
  originalDepartmentDriverType?: DepartmentDriverType | null; // Original dept driver when using fallback
  // Selected departments for "% of Selected Revenue" driver
  selectedDepartments?: string[];              // Array of department names to include in calculation
  // Source accounts for "% of Account" driver
  sourceChartOfAccountIds?: number[];          // Array of chartOfAccountIds whose forecasts are summed
  // Fallback driver for REVPAR Projection (future months)
  fallbackDriverType?: 'Budget' | 'LY Same Month' | null;  // Fallback for future months with no data
  // Zero Base driver layers (composable multi-driver)
  zeroBaseDriverLayers?: ZeroBaseDriverLayer[];
  // Labor Config per-position driver configs
  laborPositionConfigs?: LaborPositionConfig[];
  // ---- Driver-level monthly overrides (driver stays live) ----
  // Effective single-driver rate per month { jan: {...}, ... } (null-ish for composable / feed drivers)
  monthlyDriverRate?: Record<string, DriverRateInfo | null>;
  // Effective Zero Base layers / Labor Config positions per month (carry `overrides` when replaced)
  monthlyZeroBaseLayers?: Record<string, ZeroBaseDriverLayer[]>;
  monthlyLaborConfigs?: Record<string, LaborPositionConfig[]>;
  // Raw override rows per month
  monthlyDriverInputOverrides?: Record<string, DriverInputOverrideRow[]>;
  driverInputOverrideCount?: number;
}

// Revenue department for "% of Selected Revenue" driver selection
export interface RevenueDepartment {
  name: string;         // Department name
  accountCount: number; // Number of revenue accounts in this department
}

export interface AccountGroup {
  name: string;
  type: AccountType;
  total: Record<string, number>;
  children: ForecastAccount[];
  isExpanded: boolean;
}

export interface GridData {
  revenue: AccountGroup;
  expense: AccountGroup;
  netIncome: Record<string, number>;
}

// ============ Assumptions ============

export interface Assumption {
  id: number;
  name: string;
  type: AssumptionType;
  scope: AssumptionScope;
  value: string;
  numericValue?: number;
  unit?: string;
  usedBy: number;
  lastUpdated: string;
  source: DataSource;
  propertyId?: number;
  departmentId?: number;
}

// ============ Health & Setup ============

export interface HealthItem {
  id: string;
  label: string;
  value: string;
  status: HealthStatus;
  detail: string;
  lastSync?: string;
}

export interface HealthIssue {
  id: number;
  account: string;
  accountId: number;
  issue: string;
  severity: 'high' | 'medium' | 'low';
  fixAction?: string;
}

// ============ Compare / Variance ============

export interface VarianceData {
  accountId: number;
  account: string;
  department: string;
  accountType: AccountType;
  versionA: number;
  versionB: number;
  variance: number;
  variancePercent: number;
  hasDriverChange: boolean;
  hasOverrideChange: boolean;
}

export interface ComparisonSummary {
  revenueVariance: number;
  revenueVariancePercent: number;
  expenseVariance: number;
  expenseVariancePercent: number;
  netIncomeVariance: number;
  netIncomeVariancePercent: number;
}

// ============ Account Detail Panel ============

export interface AccountNote {
  id: number;
  author: string;
  authorInitials: string;
  content: string;
  createdAt: string;
  isAutoGenerated: boolean;
  eventType?: 'driver_change' | 'override' | 'value_change';
}

export interface CalculationExplanation {
  month: string;
  driverType: DriverType;
  inputs: Array<{
    label: string;
    value: string | number;
    source?: DataSource;
  }>;
  formula: string;
  result: number;
  lastSync?: string;
}

// ============ UI State Types ============

export type ActiveTab = 'workspace' | 'versions' | 'compare' | 'assumptions' | 'health' | 'notes' | 'labor' | 'risk' | 'analytics';
export type DetailPanelTab = 'driver' | 'adjustments' | 'analysis' | 'overrides' | 'notes';

export interface FilterState {
  departments: string[];
  accountTypes: AccountType[];
  driverTypes: DriverType[];
  showOnlyOverrides: boolean;
  showNeedsAttention: boolean;
  searchTerm: string;
}

// Month status for grid display (derived from version's lockedMonths)
// - 'locked': Month is in version.lockedMonths array
// - 'draft': Month is not locked and is current/past (editable)
// - 'projected': Future month with calculated projections
// - 'none': No version exists for this location/year
export type MonthStatus = 'locked' | 'draft' | 'projected' | 'none';

// Info about each month's status within the current version
// This is derived from the version's lockedMonths array and current date
export interface MonthStatusInfo {
  month: number;      // 1-12
  status: MonthStatus;
  isEditable: boolean;
}

// Legacy type for backward compatibility during transition
export interface MonthVersionInfo {
  month: number;                    // 1-12
  version: ForecastVersion | null;  // Now always the same version
  status: MonthStatus;              // Status for display
}

export interface ForecastV3State {
  // Org/Location context
  orgId: number | null;
  locationId: number | null;
  year: number;

  // Version context - single version per year with month-level locking
  currentVersion: ForecastVersion | null;  // The active version (includes lockedMonths)
  activeMonth: number | null;              // Currently focused month (1-12) for UI highlighting

  // UI state
  activeTab: ActiveTab;
  dateRange: { start: string; end: string };

  // Grid state
  selectedAccount: ForecastAccount | null;
  expandedSections: Record<string, boolean>;
  expandedColumns: Record<number, boolean>;  // Track which months are expanded (0-11)

  // Bulk selection state (for multi-account driver updates)
  isBulkMode: boolean;                     // True when bulk edit mode is active
  selectedAccountIds: number[];            // chartOfAccountIds selected for bulk edit
  bulkOperationInProgress: boolean;        // True during bulk API call

  // Detail panel
  isDetailPanelOpen: boolean;
  detailPanelTab: DetailPanelTab;

  // Filters
  filters: FilterState;

  // Loading states
  isLoading: boolean;
  isRecalculating: boolean;
  isSaving: boolean;

  // Compare mode
  compareVersionA: ForecastVersion | null;
  compareVersionB: ForecastVersion | null;

  // Pending account ID from URL (to be selected when data loads)
  pendingAccountId: number | null;
  // Pending detail panel tab from URL (to be applied after account selection)
  pendingDetailPanelTab: DetailPanelTab | null;

  // Pending department from Notes page navigation
  pendingDepartmentName: string | null;
  pendingDepartmentSection: 'Revenue' | 'Expense' | null;

  // Fullscreen mode
  isFullscreen: boolean;

  // Refresh token — increment to trigger a grid data refetch from anywhere in the tree
  refreshToken: number;
}

// ============ Action Types for Reducer ============

export type ForecastV3Action =
  | { type: 'SET_ORG_ID'; payload: number | null }
  | { type: 'SET_LOCATION_ID'; payload: number | null }
  | { type: 'SET_YEAR'; payload: number }
  | { type: 'SET_VERSION'; payload: ForecastVersion | null }
  | { type: 'SET_ACTIVE_MONTH'; payload: number | null }
  | { type: 'UPDATE_LOCKED_MONTHS'; payload: number[] }  // Update version's lockedMonths
  | { type: 'SET_ACTIVE_TAB'; payload: ActiveTab }
  | { type: 'SET_DATE_RANGE'; payload: { start: string; end: string } }
  | { type: 'SELECT_ACCOUNT'; payload: ForecastAccount | null }
  | { type: 'UPDATE_SELECTED_ACCOUNT_DATA'; payload: ForecastAccount | null }  // Update account without resetting tab
  | { type: 'TOGGLE_SECTION'; payload: string }
  | { type: 'TOGGLE_DETAIL_PANEL'; payload?: boolean }
  | { type: 'SET_DETAIL_PANEL_TAB'; payload: DetailPanelTab }
  | { type: 'SET_FILTERS'; payload: Partial<FilterState> }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_RECALCULATING'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_COMPARE_VERSIONS'; payload: { a: ForecastVersion | null; b: ForecastVersion | null } }
  // Bulk selection actions
  | { type: 'TOGGLE_BULK_MODE'; payload?: boolean }
  | { type: 'TOGGLE_ACCOUNT_SELECTION'; payload: number }  // chartOfAccountId
  | { type: 'SET_SELECTED_ACCOUNTS'; payload: number[] }   // Replace selection
  | { type: 'ADD_SELECTED_ACCOUNTS'; payload: number[] }   // Add to selection
  | { type: 'CLEAR_BULK_SELECTION' }
  | { type: 'SET_BULK_OPERATION_IN_PROGRESS'; payload: boolean }
  // Column expansion actions
  | { type: 'TOGGLE_COLUMN_EXPANSION'; payload: number }   // monthIndex (0-11)
  | { type: 'SET_ALL_COLUMNS_EXPANDED'; payload: boolean }
  // URL state actions
  | { type: 'SET_PENDING_ACCOUNT_ID'; payload: number | null }
  | { type: 'SET_PENDING_DETAIL_PANEL_TAB'; payload: DetailPanelTab | null }
  // Department navigation actions
  | { type: 'SET_PENDING_DEPARTMENT'; payload: { name: string; section: 'Revenue' | 'Expense' } | null }
  // Fullscreen mode
  | { type: 'SET_FULLSCREEN'; payload: boolean }
  // Trigger a data refetch from any component in the tree
  | { type: 'TRIGGER_REFRESH' };

// ============ API Response Types ============

export interface ForecastPnLDataV2 {
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  accountType: string;
  department: string;
  monthNumber: number;
  year: number;
  budgetAmount: number;
  actualAmount: number;
  calculatedForecastAmount: number;
  savedForecastAmount: number;
  driverType: string;
  driverInput: number;
  roomsAvailable: number;
  roomsOccupied: number;
  occupancyRate: number;
  adr: number;
  revpar: number;
}

export interface CreateVersionRequest {
  name: string;
  baseVersionId: number;
  type: VersionType;
  startDate: string;
  endDate: string;
  copyOptions: {
    drivers: boolean;
    assumptions: boolean;
    overrides: boolean;
    notes: boolean;
  };
}

export interface UpdateDriverRequest {
  accountId: number;
  versionId: number;
  driverType: DriverType;
  driverInput: number | null;
  monthRange?: { start: number; end: number };
}

// Bulk driver update types
export interface BulkDriverAssignment {
  chartOfAccountId: number;
  driverType: string;
  driverInput: number | null;
  inputMode?: 'monthly' | 'daily';
  effectiveStartMonth?: number;
  effectiveEndMonth?: number;
}

export interface BulkDriverRequest {
  versionId: number;
  assignments: BulkDriverAssignment[];
}

export interface BulkDriverResult {
  chartOfAccountId: number;
  status: 'success' | 'error';
  error?: string;
}

export interface BulkDriverResponse {
  success: boolean;
  updated: number;
  failed: number;
  results: BulkDriverResult[];
}

// ============ Department Driver Types ============

// Limited driver types for department level (only these are allowed)
// OTB, RMS, and percentage drivers can be distributed proportionally among accounts
// based on their past performance (revenue for Revenue accounts, LY actuals for Expense accounts).
export type DepartmentDriverType = 'Room Forecast' | 'OTB Forecast' | 'RMS Forecast' | '% of Revenue' | '% of Selected Revenue';

// Fallback drivers for future months when OTB/RMS data isn't available
export type FallbackDriverType = 'LY Same Month' | 'Budget' | null;

export interface DepartmentDriver {
  id: number;
  versionId: number;
  locationId: number;
  departmentName: string;
  driverType: DepartmentDriverType;
  driverInput: number | null;
  inputMode: string;
  effectiveStartMonth: number;
  effectiveEndMonth: number;
  otbLeadDays: number;  // Days before month to start using OTB (default -5)
  fallbackDriverType: FallbackDriverType;  // What to use outside OTB window
  selectedDepartments?: string[];  // For "% of Selected Revenue" - which revenue departments to include
  createdAt?: string;
  updatedAt?: string;
}

// Options for clearing child account data when applying department driver
export interface CascadeOptions {
  clearAccountDrivers: boolean;
  clearAccountOverrides: boolean;
  clearAccountAdjustments: boolean;
  clearAccountNotes: boolean;
}

// Info about an account's status within a department (for the panel)
export interface DepartmentAccountInfo {
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  hasAccountDriver: boolean;
  accountDriverType: DriverType | null;
  inheritsFromDepartment: boolean;
  hasOverrides: boolean;
  hasAdjustments: boolean;
}

// Request to save department driver
export interface SaveDepartmentDriverRequest {
  versionId: number;
  locationId: number;
  departmentName: string;
  driverType: DepartmentDriverType;
  driverInput?: number | null;
  effectiveStartMonth?: number;
  effectiveEndMonth?: number;
  otbLeadDays?: number;  // Days before month to start using OTB (default -5)
  fallbackDriverType?: FallbackDriverType;  // What to use outside OTB window
  cascadeOptions?: CascadeOptions;
}

// Response from saving department driver
export interface SaveDepartmentDriverResponse {
  success: boolean;
  departmentDriver: DepartmentDriver;
  affectedAccounts?: number;
}

export interface RecalculationPreview {
  affectedAccounts: number;
  affectedMonths: number;
  impacts: Array<{
    accountId: number;
    accountName: string;
    monthRange: string;
    beforeTotal: number;
    afterTotal: number;
    delta: number;
  }>;
}

// ============ Component Props Types ============

export interface StatusPillProps {
  status: VersionStatus;
  size?: 'sm' | 'md';
}

export interface DriverBadgeProps {
  driver: DriverType;
  size?: 'sm' | 'md';
}

export interface GridRowProps {
  account: ForecastAccount;
  isSelected: boolean;
  onSelect: (account: ForecastAccount) => void;
  months: string[];
}

export interface AccountDetailPanelProps {
  account: ForecastAccount | null;
  onClose: () => void;
  onDriverChange: (driverType: DriverType, input: number | null) => void;
  onOverrideChange: (monthIndex: number, value: number | null) => void;
}

// ============ AI Build (Build with AI) Types ============

// Driver suggestion produced by the AI build analysis for a single account
export interface AiBuildSuggestion {
  driverType: string;
  input: number | null;
  reason: string;
  correlation: number | null;
  classification: string | null;   // e.g. 'fixed' for fixed-cost accounts
  selectedDepartments: string[] | null;  // For "% of Selected Revenue" suggestions
  // Budget-mode per-position Labor Config payload (driverType 'Labor Config')
  laborPositionConfigs?: LaborPositionConfig[] | null;
}

// Budget-mode rooms baseline proposed by the preview and persisted on commit
export interface AiBuildRoomStatSeed {
  seed: Record<string, { occupiedRooms: number; availableRooms?: number }>;  // 'YYYY-MM' keys
  sources: Record<string, string>;  // 'pms-actual' | 'ly-budget' | 'pms-extrapolated' | 'prior-year' | 'none'
  baseYear: number;
  seededMonths: number;
}

// One proposed driver assignment (with pre-calculated forecast values)
export interface AiBuildProposal {
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  department: string;
  accountType: string;
  suggestion: AiBuildSuggestion;
  monthlyValues: number[];   // 12 values, Jan-Dec
  annualTotal: number;
  explanation: string;
}

// Account the analysis could not produce a suggestion for
export interface AiBuildUnassignedAccount {
  chartOfAccountId: number;
  accountName: string;
  reason: string;
}

export interface AiBuildSummary {
  proposedCount: number;
  unassignedWithoutSuggestion: AiBuildUnassignedAccount[];
  monthsCalculated: number;
  // Budget-mode extras (versionType 'budget')
  budgetMode?: boolean;
  roomStatSeed?: AiBuildRoomStatSeed | null;
}

// Payload of the `complete` SSE event from ?action=aiBuildPreview
export interface AiBuildPreviewResult {
  proposals: AiBuildProposal[];
  summary: AiBuildSummary;
}

// Payload of the `progress` SSE events from ?action=aiBuildPreview
export interface AiBuildProgress {
  stage: 'analyzing' | 'arbitrating' | 'calculating';
  accountCount?: number;
}

// ============ AI Rooms Forecast Types ============

// One stay date in the AI rooms forecast preview (?action=aiRoomsForecastPreview).
// Days beyond the OTB horizon are absent; isActualized rows are past dates shown for context.
export interface AiRoomsForecastDay {
  date: string;          // YYYY-MM-DD stay date
  dow: number;           // day of week
  daysOut: number;       // days between asOf and the stay date
  otbRooms: number;
  otbRevenue: number;
  pickupRooms: number;   // expected additional rooms from the learned pickup curve
  pickupRevenue: number;
  forecastRooms: number; // otbRooms + pickupRooms (actuals when isActualized)
  forecastRevenue: number;
  adr: number;
  isActualized: boolean;
  // How the day was forecast: pace (learned pickup), blend (pickup shrunk
  // toward the reference), anchored (no pickup history — reference final),
  // otb (nothing to anchor to), actual
  method?: 'pace' | 'blend' | 'anchored' | 'otb' | 'actual';
  referenceRooms?: number | null;
  referenceSource?: AiRoomsReferenceSource | null;
  paceWeight?: number | null;
}

// Where an anchored day's expected final came from
export type AiRoomsReferenceSource = 'ly' | 'budget' | 'recent';

// Per-month rollup of the daily forecast, keyed by 'YYYY-MM'
export interface AiRoomsForecastMonthSummary {
  roomNights: number;
  revenue: number;
  adr: number;
  daysCovered: number;
  futureDays?: number;
  pickupSupportPct?: number;  // share of future days the pickup curve has history for (0-1)
  anchoredDays?: number;      // future days with no pickup history — reference final only
  blendDays?: number;         // future days where thin pickup is shrunk toward the reference
  forecastablePct?: number;   // share of future days with pace or a reference (0-1)
  referenceSource?: AiRoomsReferenceSource | null;  // most common reference in the month
}

// Rooms revenue account the applied forecast is distributed across
export interface AiRoomsForecastTargetAccount {
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  department: string;
  chartOfAccountDepartmentId: number | null;
  trailingRevenue: number;
  hasAccountDriver: boolean;  // own account-level driver — won't inherit the department driver
}

export interface AiRoomsForecastTargetDepartment {
  department: string;
  chartOfAccountDepartmentId: number | null;
}

export interface AiRoomsForecastPreviewAvailable {
  available: true;
  days: AiRoomsForecastDay[];
  monthly: Record<string, AiRoomsForecastMonthSummary>;
  curveSample: number;   // number of historical stay dates the pickup curve was learned from
  asOf: string;
  targetDepartments: AiRoomsForecastTargetDepartment[];  // get the Room Forecast driver on apply
  targetAccounts: AiRoomsForecastTargetAccount[];  // sorted by trailing revenue desc
  curveInsufficient?: boolean;  // no pickup history yet — everything future is anchored
  reference?: { roomsTrend: number; adrTrend: number; alignedDays: number };
  sourceStatus?: {
    active: boolean;               // this version's rooms driver already reads the AI source
    lastRefreshedAt: string | null;
    appliedMonths: string[];       // 'YYYY-MM' months the background refresh keeps current
  };
  // Open months where manual $ overrides on the target accounts would keep
  // revenue from following the forecast (apply can clear them)
  overrides?: { monthKey: string; accounts: number; total: number }[];
}

export interface AiRoomsForecastPreviewUnavailable {
  available: false;
  reason: string;
}

export type AiRoomsForecastPreviewResult =
  | AiRoomsForecastPreviewAvailable
  | AiRoomsForecastPreviewUnavailable;

// ============ Utility Types ============

export const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;
export const MONTH_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;

export type MonthKey = typeof MONTHS[number];

// Driver type display configuration
// applicableTo: 'both' | 'revenue' | 'expense'
//   'revenue'  — only valid for Revenue accounts/departments
//   'expense'  — only valid for Expense accounts/departments
//   'both'     — valid for either type
const L = (k: string) => DRIVER_LABELS[k]?.short ?? k;
// Badge text comes from the canonical vocabulary in ./driverTerms — edit labels THERE.
export const DRIVER_CONFIG: Record<string, { color: string; bgColor: string; label: string; applicableTo: 'both' | 'revenue' | 'expense' }> = {
  // Manual/Static
  'Manual': { color: 'text-slate-600', bgColor: 'bg-slate-100', label: L('Manual'), applicableTo: 'both' },
  'Static Entry': { color: 'text-slate-600', bgColor: 'bg-slate-100', label: L('Static Entry'), applicableTo: 'both' },
  // Budget
  'LY Published Budget': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: 'LY Published Budget', applicableTo: 'both' },
  'LY Published Budget $POR': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: 'LY Published Budget $POR', applicableTo: 'both' },
  'LY Published Budget $PAR': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: 'LY Published Budget $PAR', applicableTo: 'both' },
  '% of Total Revenue (LY Budget)': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: '% of Total Revenue (LY Budget)', applicableTo: 'expense' },
  '% of Selected Revenue (LY Budget)': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: '% of Selected Revenue (LY Budget)', applicableTo: 'both' },
  'Budget': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: L('Budget'), applicableTo: 'both' },
  // Room-based
  '$ / ORN': { color: 'text-blue-700', bgColor: 'bg-blue-50', label: L('$ / ORN'), applicableTo: 'both' },
  '$ / ARN': { color: 'text-blue-700', bgColor: 'bg-blue-50', label: L('$ / ARN'), applicableTo: 'both' },
  'REVPAR Projection': { color: 'text-blue-700', bgColor: 'bg-blue-50', label: L('REVPAR Projection'), applicableTo: 'revenue' },
  'Occupancy Forecast': { color: 'text-blue-700', bgColor: 'bg-blue-50', label: L('Occupancy Forecast'), applicableTo: 'revenue' },
  // Last Month
  '$ LM': { color: 'text-cyan-700', bgColor: 'bg-cyan-50', label: L('$ LM'), applicableTo: 'both' },
  '$ / ORN LM': { color: 'text-cyan-700', bgColor: 'bg-cyan-50', label: L('$ / ORN LM'), applicableTo: 'both' },
  '$ / ARN LM': { color: 'text-cyan-700', bgColor: 'bg-cyan-50', label: L('$ / ARN LM'), applicableTo: 'both' },
  // External Sources
  'OTB Forecast': { color: 'text-emerald-700', bgColor: 'bg-emerald-50', label: L('OTB Forecast'), applicableTo: 'both' },
  'RMS Forecast': { color: 'text-emerald-700', bgColor: 'bg-emerald-50', label: L('RMS Forecast'), applicableTo: 'both' },
  'Room Forecast': { color: 'text-emerald-700', bgColor: 'bg-emerald-50', label: L('Room Forecast'), applicableTo: 'both' },
  // Revenue % — only makes sense for expense accounts (cost as % of revenue)
  '% of Revenue': { color: 'text-purple-700', bgColor: 'bg-purple-50', label: L('% of Revenue'), applicableTo: 'expense' },
  // Revenue accounts may use % of Selected Revenue (other departments — the calculator excludes the account itself);
  // % of TOTAL revenue stays expense-only (circular for revenue). Rules mirror
  // azfApi/utils/forecastCalculatorV3/drivers/driverEligibility.js — the backend refuses ineligible assignments.
  '% of Selected Revenue': { color: 'text-purple-700', bgColor: 'bg-purple-50', label: L('% of Selected Revenue'), applicableTo: 'both' },
  // Last Year / Historical
  'LY Same Month': { color: 'text-amber-700', bgColor: 'bg-amber-50', label: L('LY Same Month'), applicableTo: 'both' },
  'YoY Growth %': { color: 'text-amber-700', bgColor: 'bg-amber-50', label: L('YoY Growth %'), applicableTo: 'both' },
  '$ / ORN LY': { color: 'text-amber-700', bgColor: 'bg-amber-50', label: L('$ / ORN LY'), applicableTo: 'both' },
  '$ / ARN LY': { color: 'text-amber-700', bgColor: 'bg-amber-50', label: L('$ / ARN LY'), applicableTo: 'both' },
  // Budget-based drivers
  '% of Selected Revenue (Budgeted)': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: L('% of Selected Revenue (Budgeted)'), applicableTo: 'both' },
  '% of Total Revenue (Budgeted)': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: L('% of Total Revenue (Budgeted)'), applicableTo: 'expense' },
  'Budgeted $POR': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: L('Budgeted $POR'), applicableTo: 'both' },
  'Budgeted $PAR': { color: 'text-indigo-700', bgColor: 'bg-indigo-50', label: L('Budgeted $PAR'), applicableTo: 'both' },
  // Account-based
  '% of Account': { color: 'text-teal-700', bgColor: 'bg-teal-50', label: L('% of Account'), applicableTo: 'both' },
  // Labor drivers
  'HPOR': { color: 'text-rose-700', bgColor: 'bg-rose-50', label: L('HPOR'), applicableTo: 'both' },
  'DPOR': { color: 'text-rose-700', bgColor: 'bg-rose-50', label: L('DPOR'), applicableTo: 'both' },
  '$POR Projection': { color: 'text-rose-700', bgColor: 'bg-rose-50', label: L('$POR Projection'), applicableTo: 'both' },
  'Avg Rate x Hours': { color: 'text-rose-700', bgColor: 'bg-rose-50', label: L('Avg Rate x Hours'), applicableTo: 'both' },
  'Labor Config': { color: 'text-rose-700', bgColor: 'bg-rose-50', label: L('Labor Config'), applicableTo: 'expense' },
  // Zero Base composable driver
  'Zero Base': { color: 'text-orange-700', bgColor: 'bg-orange-50', label: L('Zero Base'), applicableTo: 'expense' },
};

// Status display configuration — aligned with Lodgeic Design System semantic colors
export const STATUS_CONFIG: Record<VersionStatus, { color: string; bgColor: string; borderColor: string }> = {
  'Draft': { color: 'text-amber-700', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },   // warning
  'Locked': { color: 'text-slate-600', bgColor: 'bg-slate-100', borderColor: 'border-slate-200' },  // neutral
  'Published': { color: 'text-emerald-700', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' }, // success
};

export { DRIVER_LABELS, TERMS, driverLabel, driverShortLabel } from './driverTerms';
